import React, { useEffect, useRef, useState } from 'react';
import { CheckIcon, MailIcon } from 'lucide-react';
import { Shell, Section, Eyebrow, GoldRule } from '../../components/ui/Layout';
import { Button } from '../../components/ui/Button';
import { useRegistry } from '../../contexts/RegistryContext';
import { applyPageMeta } from '../../utils/seo';
const field = 'mt-2 w-full border border-navy-100 bg-white px-3.5 py-2.5 font-sans text-[0.9375rem] text-ink placeholder:text-charcoal-200 transition-colors duration-120 hover:border-navy-300 focus:border-navy-500';
const labelClass = 'block font-sans text-[0.8125rem] font-medium text-ink';
export function Contact() {
  const {
    startThread
  } = useRegistry();
  const [form, setForm] = useState({
    contactName: '',
    contactEmail: '',
    organization: '',
    subject: '',
    body: ''
  });
  const [reference, setReference] = useState('');
  const confirmationRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    applyPageMeta({
      title: 'Contact | Velnoris Executive Network',
      description: 'Message Velnoris Executive Network directly. Search inquiries, candidate support, privacy requests and technical support each receive a reference number.',
      canonicalPath: '/contact'
    });
  }, []);
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(prev => ({
    ...prev,
    [key]: e.target.value
  }));
  if (reference) {
    return <main id="main" data-id="element-689" data-mp="3svkab1adcts9">
        <Section tone="parchment" data-id="element-690" data-mp="1izeeo3m9ncf">
          <div ref={confirmationRef} tabIndex={-1} className="max-w-2xl animate-rise-in border-t-2 border-ink bg-white p-8 outline-none md:p-12" data-id="element-691" data-mp="1k10roq31w9g">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink" data-id="element-692" data-mp="3nxsg20m94vkw">
              <CheckIcon aria-hidden="true" className="h-5 w-5" data-id="element-693" data-mp="3a03xizfnq0j0" />
            </span>
            <h1 className="mt-6 font-serif text-[1.75rem] leading-[1.15] text-ink md:text-[2.25rem]" data-id="element-694" data-mp="3ey5gzlgzw1v0">Message received</h1>
            <p className="mt-4 font-serif text-[1.0625rem] leading-relaxed text-charcoal-700" data-id="element-695" data-mp="1kigr4kzdp1e7">
              Thank you. Your reference is <span className="font-sans text-[0.9375rem] text-ink" data-id="element-696" data-mp="1fougeycrz7l1">{reference}</span>. A
              consultant reads every message personally and will reply to {form.contactEmail}.
            </p>
            <p className="mt-6 font-sans text-[0.8125rem] text-charcoal-500" data-id="element-697" data-mp="16o61m1fk5373">
              If it is urgent, write to{' '}
              <a href="mailto:hello@velnorisexecutivenetwork.com" className="text-ink underline underline-offset-2" data-id="element-698" data-mp="1zf7jiaamxcub">
                hello@velnorisexecutivenetwork.com
              </a>{' '}
              quoting that reference.
            </p>
          </div>
        </Section>
      </main>;
  }
  return <main id="main" data-id="element-699" data-mp="j17mb736c1ar">
      <div className="on-dark bg-ink" data-id="element-700" data-mp="j1pqcpw2vjt8">
        <Shell className="py-14 md:py-20" data-id="element-701" data-mp="iylh3e3r9xvp">
          <div className="max-w-2xl animate-rise-in" data-id="element-702" data-mp="e11zq5brh4zj">
            <GoldRule data-id="element-703" data-mp="e3ob6g1rw4n1" />
            <Eyebrow onDark className="mt-5" data-id="element-704" data-mp="3xwr5lqxrdrl">
              Contact
            </Eyebrow>
            <h1 className="mt-3 font-serif text-[2rem] leading-[1.12] tracking-[-0.015em] text-parchment md:text-[2.75rem]" data-id="element-705" data-mp="8wr78fxiqo80">
              Start a conversation
            </h1>
            <p className="mt-5 font-serif text-[1.0625rem] leading-relaxed text-navy-100" data-id="element-706" data-mp="duj7bx8bhgna">
              Whether you are considering a search, exploring your next move, or need help with something on this site —
              write to us here. Every message is read by a person and answered.
            </p>
          </div>
        </Shell>
      </div>

      <Section tone="parchment" data-id="element-707" data-mp="wm2coaptd6sh">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-16" data-id="element-708" data-mp="wmv9ojyz0hxf">
          <form className="border-t-2 border-ink bg-white p-6 md:p-10" onSubmit={e => {
          e.preventDefault();
          const thread = startThread({
            contactName: form.contactName.trim(),
            contactEmail: form.contactEmail.trim(),
            organization: form.organization.trim(),
            subject: form.subject.trim() || 'General inquiry',
            body: form.body.trim()
          });
          setReference(thread.reference);
          window.requestAnimationFrame(() => confirmationRef.current?.focus());
        }} data-id="element-709" data-mp="11kdmilg3zhap">
            <h2 className="font-serif text-[1.375rem] text-ink" data-id="element-710" data-mp="11o06j3dd7idx">Send a message</h2>
            <div className="mt-8 space-y-6" data-id="element-711" data-mp="e11qu7x2keh2">
              <div className="grid gap-6 sm:grid-cols-2" data-id="element-712" data-mp="9z6s9b6iwf6z">
                <div data-id="element-713" data-mp="a0ror6cee87h">
                  <label htmlFor="c-name" className={labelClass} data-id="element-714" data-mp="9x3to2rb1dqn">
                    Your name <span className="text-gold-700" data-id="element-715" data-mp="nrmptwgnnl0i">*</span>
                  </label>
                  <input id="c-name" required value={form.contactName} onChange={set('contactName')} autoComplete="name" className={field} data-id="element-716" data-mp="iy1z8phe5tbc" />
                </div>
                <div data-id="element-717" data-mp="3mgv9asayo00r">
                  <label htmlFor="c-email" className={labelClass} data-id="element-718" data-mp="3mfahhag16v6l">
                    Email <span className="text-gold-700" data-id="element-719" data-mp="21xrhft6y5aea">*</span>
                  </label>
                  <input id="c-email" type="email" required value={form.contactEmail} onChange={set('contactEmail')} autoComplete="email" className={field} data-id="element-720" data-mp="26oztkwznv9v5" />
                </div>
              </div>
              <div data-id="element-721" data-mp="1xwph191amsaq">
                <label htmlFor="c-org" className={labelClass} data-id="element-722" data-mp="1xw64y8ybqy4m">
                  Organization
                </label>
                <input id="c-org" value={form.organization} onChange={set('organization')} autoComplete="organization" className={field} data-id="element-723" data-mp="2lnbken0croee" />
              </div>
              <div data-id="element-724" data-mp="2bo2wobw2z60h">
                <label htmlFor="c-subject" className={labelClass} data-id="element-725" data-mp="2bom777jeis4h">
                  Subject
                </label>
                <input id="c-subject" value={form.subject} onChange={set('subject')} placeholder="Search inquiry, candidate support, privacy request…" className={field} data-id="element-726" data-mp="2gm4hb195nde2" />
              </div>
              <div data-id="element-727" data-mp="158abdd41xmc2">
                <label htmlFor="c-body" className={labelClass} data-id="element-728" data-mp="159lhvkovt86v">
                  Message <span className="text-gold-700" data-id="element-729" data-mp="l65td8qmzvg2">*</span>
                </label>
                <textarea id="c-body" required rows={6} value={form.body} onChange={set('body')} className={`${field} resize-y`} data-id="element-730" data-mp="h4kly2wvg9d9" />
              </div>
              <div className="border-t border-navy-100 pt-6" data-id="element-731" data-mp="cc16t1ijd66c">
                <Button type="submit" size="lg" data-id="element-732" data-mp="7far4fan8clg">
                  Send message
                </Button>
                <p className="mt-4 font-sans text-[0.75rem] leading-relaxed text-charcoal-500" data-id="element-733" data-mp="2focc1f62oaj">
                  We use what you send only to answer you. Please do not include government identifiers, banking details
                  or passwords — we never need them.
                </p>
              </div>
            </div>
          </form>

          <aside data-id="element-734" data-mp="3kn42w9a3nssr">
            <GoldRule data-id="element-735" data-mp="1nsr57ygpwtpe" />
            <h2 className="mt-5 font-serif text-[1.25rem] text-ink" data-id="element-736" data-mp="1ntanxhe6zm4h">Other ways to reach us</h2>
            <p className="mt-5 flex items-start gap-2.5 font-sans text-[0.875rem] leading-relaxed text-charcoal-700" data-id="element-737" data-mp="1it4t5scmlji6">
              <MailIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-gold-700" data-id="element-738" data-mp="1qc5p9sl3kavi" />
              <a href="mailto:hello@velnorisexecutivenetwork.com" className="underline underline-offset-2" data-id="element-739" data-mp="1vbidagnpfnzo">
                hello@velnorisexecutivenetwork.com
              </a>
            </p>
            <p className="mt-6 border-l-2 border-navy-100 pl-5 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-740" data-mp="252cxlfhe4dih">
              One Sterling Plaza, London EC2
            </p>
            <div className="mt-10 border-t border-navy-100 pt-6" data-id="element-741" data-mp="1cd62yrl2482i">
              <h3 className="font-serif text-[1rem] text-ink" data-id="element-742" data-mp="1cfiheurlw0at">Before you write</h3>
              <p className="mt-3 font-sans text-[0.8125rem] leading-relaxed text-charcoal-500" data-id="element-743" data-mp="1hb7i51fd3n8p">
                We never request payment, banking details or identity documents by email. If you have received a message
                claiming to be from us and asking for any of those, please tell us here.
              </p>
            </div>
          </aside>
        </div>
      </Section>
    </main>;
}